import React from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  marketingColor,
  marketingFont,
  marketingLayout,
  marketingLine,
  marketingShadow,
} from '../../theme/marketingTokens';
import { Eyebrow, MarketingImageSlot, marketingImages } from './MarketingPrimitives';
import { PIPELINE_STAGE_HEIGHT, prefersReducedMotion } from './useMarketingScroll';

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));
/** Normalised progress through the window [a, b]. */
const ramp = (p: number, a: number, b: number) => clamp01((p - a) / (b - a));
const lerp = (t: number, a: number, b: number) => a + (b - a) * t;

/**
 * Camera motion, keyed off scene progress.
 *
 * Transcribed from the handoff's keyframe table: the raw photo starts large and
 * centred, shrinks onto the pipeline line, travels down through the two
 * enrichment nodes, then settles as the storefront listing's product image.
 */
const cameraTransform = (p: number): string => {
  let x = 0;
  let y = 30;
  let scale = 1;
  if (p < 0.2) {
    x = 0; y = 30; scale = 1;
  } else if (p < 0.38) {
    const t = ramp(p, 0.2, 0.38);
    y = lerp(t, 30, 156);
    scale = lerp(t, 1, 0.3);
  } else if (p < 0.58) {
    y = lerp(ramp(p, 0.38, 0.58), 156, 290);
    scale = 0.3;
  } else if (p < 0.78) {
    y = lerp(ramp(p, 0.58, 0.78), 290, 400);
    scale = 0.3;
  } else {
    const t = ramp(p, 0.78, 1);
    x = lerp(t, 0, -184);
    y = lerp(t, 400, 396);
    scale = lerp(t, 0.3, 0.68);
  }
  return `translate(${x}px, ${y}px) scale(${scale})`;
};

const codeCardSx = {
  backgroundColor: marketingColor.ink,
  borderRadius: '6px',
  px: 2,
  py: 1.75,
  fontFamily: marketingFont.mono,
  fontSize: 11.5,
  lineHeight: 1.65,
  color: marketingColor.textSecondary,
} as const;

const StructuredCard: React.FC = () => (
  <>
    <Box sx={{ color: marketingColor.accent, mb: 0.5 }}>{'// structured'}</Box>
    <Box>{'{ "brand": "Sony",'}</Box>
    <Box sx={{ pl: 2 }}>{'"model": "Alpha 7 IV",'}</Box>
    <Box sx={{ pl: 2 }}>{'"price": null }'}</Box>
  </>
);

const EnrichedCard: React.FC = () => (
  <>
    <Box sx={{ color: marketingColor.accent, mb: 0.5 }}>[AI] mozi-enrich-v2</Box>
    <Box sx={{ color: marketingColor.accent, fontWeight: 700 }}>+ "price": 2199.99</Box>
    <Box sx={{ color: marketingColor.accent, fontWeight: 700 }}>+ "mapPrice": 2199.99</Box>
    <Box sx={{ color: marketingColor.accent, fontWeight: 700 }}>
      {'+ "specs": { "sensor": "33MP" }'}
    </Box>
  </>
);

/** The finished storefront listing — the payoff at the end of the scene. */
const StorefrontCard: React.FC<{ showProductImage?: boolean }> = ({
  showProductImage = false,
}) => {
  const { t } = useTranslation() as { t: (key: string) => string };
  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: marketingColor.ink,
        border: `1px solid ${marketingLine.hairlineStrong}`,
        borderRadius: '8px',
        p: 2.5,
        display: 'flex',
        gap: 2.75,
        boxShadow: marketingShadow.panel,
      }}
    >
      <Box
        sx={{
          width: 200,
          height: 200,
          flexShrink: 0,
          borderRadius: '6px',
          backgroundColor: marketingColor.surface,
          border: `1px solid rgba(95,226,138,0.2)`,
          overflow: 'hidden',
          display: { xs: 'none', sm: 'block' },
        }}
      >
        {showProductImage ? (
          <MarketingImageSlot
            src={marketingImages.pipelineCamera}
            fallbackSrc={marketingImages.pipelineCameraFallback}
            alt={t('public.home.pipeline.cameraAlt')}
            placeholder=""
            sx={{ width: '100%', height: '100%', backgroundColor: 'transparent' }}
            imageSx={{ objectFit: 'contain', p: 2 }}
          />
        ) : null}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, fontFamily: marketingFont.mono }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 0.75,
          }}
        >
          <Box
            sx={{
              fontSize: 10,
              color: marketingColor.textMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            yourstore.myshopify.com
          </Box>
          <Box
            sx={{
              fontSize: 9.5,
              color: marketingColor.accent,
              border: `1px solid ${marketingLine.hairlineStrong}`,
              borderRadius: '3px',
              px: 0.9,
              py: 0.25,
              whiteSpace: 'nowrap',
            }}
          >
            {t('public.home.pipeline.listing.inStock')}
          </Box>
        </Box>
        <Typography
          sx={{
            fontFamily: marketingFont.display,
            fontSize: 20,
            fontWeight: 700,
            color: marketingColor.text,
            mb: 0.4,
          }}
        >
          Sony Alpha 7 IV Full-Frame Mirrorless Camera
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box sx={{ fontSize: 10.5, color: marketingColor.textMuted }}>SKU ILCE7M4MB</Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mb: 1.25, flexWrap: 'wrap' }}>
          <Box sx={{ fontSize: 18, fontWeight: 700, color: marketingColor.accent }}>$2,199.99</Box>
          <Box
            sx={{ fontSize: 12, color: marketingColor.textMuted, textDecoration: 'line-through' }}
          >
            $2,699.99
          </Box>
          <Box
            sx={{
              fontSize: 10,
              color: marketingColor.warning,
              border: `1px solid ${marketingLine.warning}`,
              borderRadius: '3px',
              px: 0.75,
              py: 0.25,
            }}
          >
            {t('public.home.pipeline.listing.save')}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', mb: 1.5 }}>
          {[
            '• 33MP full-frame BSI sensor',
            '• 4K 60p video',
            '• 5-axis in-body stabilization',
            '• 10 fps burst',
          ].map((spec) => (
            <Box key={spec} sx={{ fontSize: 11, color: marketingColor.textSecondary }}>
              {spec}
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box
            sx={{
              backgroundColor: marketingColor.accent,
              color: marketingColor.ink,
              fontFamily: marketingFont.display,
              fontWeight: 700,
              fontSize: 12,
              px: 2.5,
              py: 1.1,
              borderRadius: '3px',
            }}
          >
            Add to cart
          </Box>
          <Box sx={{ fontSize: 10.5, color: marketingColor.accent }}>
            ✓ synced by Motzu · MAP compliant
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const SceneHeading: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string) => string };
  return (
    <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
      <Eyebrow sx={{ mb: 2 }}>{t('public.home.pipeline.eyebrow')}</Eyebrow>
      <Typography variant="h3" sx={{ fontSize: { xs: '1.65rem', md: '1.875rem' }, mb: 1 }}>
        {t('public.home.pipeline.title')}
      </Typography>
      <Typography sx={{ fontSize: 13.5, color: marketingColor.textSecondary }}>
        {t('public.home.pipeline.subtitle')}
      </Typography>
    </Box>
  );
};

/**
 * Narrow-viewport and reduced-motion fallback.
 *
 * The pinned scene assumes desktop height and a scroll budget neither case has,
 * so the same four beats are laid out as a static vertical sequence.
 */
const StackedPipeline: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string) => string };
  const connector = (
    <Box
      aria-hidden
      sx={{ width: '2px', height: 32, backgroundColor: marketingColor.accent, mx: 'auto' }}
    />
  );
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 620, mx: 'auto' }}>
      <MarketingImageSlot
        src={marketingImages.pipelineCamera}
        fallbackSrc={marketingImages.pipelineCameraFallback}
        alt={t('public.home.pipeline.cameraAlt')}
        placeholder={t('public.home.pipeline.cameraPlaceholder')}
        sx={{
          width: 220,
          height: 220,
          borderRadius: '10px',
          border: `2px solid ${marketingColor.accent}`,
          boxShadow: `${marketingShadow.card}, 0 0 30px rgba(95,226,138,0.15)`,
        }}
        imageSx={{ objectFit: 'contain', p: 3 }}
      />
      {connector}
      <Box sx={{ ...codeCardSx, width: '100%', border: `1px solid ${marketingLine.hairlineStrong}` }}>
        <StructuredCard />
      </Box>
      {connector}
      <Box
        sx={{
          ...codeCardSx,
          width: '100%',
          border: `1px solid ${marketingColor.accent}`,
          boxShadow: '0 0 24px rgba(95,226,138,0.18)',
        }}
      >
        <EnrichedCard />
      </Box>
      {connector}
      <StorefrontCard showProductImage />
    </Box>
  );
};

interface PipelineSceneProps {
  sectionRef: React.RefObject<HTMLDivElement | null>;
  progress: number;
  stageScale: number;
}

export const PipelineScene: React.FC<PipelineSceneProps> = ({
  sectionRef,
  progress,
  stageScale,
}) => {
  const { t } = useTranslation() as { t: (key: string) => string };
  const wideEnough = useMediaQuery('(min-width:900px)');
  const pinned = wideEnough && !prefersReducedMotion();

  if (!pinned) {
    return (
      <Box
        component="section"
        ref={sectionRef}
        sx={{
          backgroundColor: marketingColor.surface,
          borderTop: `1px solid ${marketingLine.hairline}`,
          borderBottom: `1px solid ${marketingLine.hairline}`,
          px: marketingLayout.sectionPaddingX,
          py: { xs: 7, md: 9 },
        }}
      >
        <Box sx={{ mb: 5 }}>
          <SceneHeading />
        </Box>
        <StackedPipeline />
      </Box>
    );
  }

  const p = progress;
  const dotLit = (threshold: number) => p > threshold;
  const dotSx = (threshold: number) =>
    ({
      position: 'absolute',
      left: '50%',
      width: 10,
      height: 10,
      ml: '-5px',
      borderRadius: '999px',
      backgroundColor: dotLit(threshold) ? marketingColor.accent : marketingColor.surfaceStrong,
      boxShadow: dotLit(threshold) ? `0 0 12px ${marketingColor.accent}` : 'none',
    } as const);

  // Both side cards fade back slightly as the storefront card takes over.
  const settle = 1 - ramp(p, 0.88, 1) * 0.6;
  const jsonReveal = ramp(p, 0.3, 0.42);
  const aiReveal = ramp(p, 0.52, 0.64);
  const shopReveal = ramp(p, 0.74, 0.88);

  return (
    <Box
      component="section"
      ref={sectionRef}
      sx={{
        position: 'relative',
        height: '230vh',
        backgroundColor: marketingColor.surface,
        borderTop: `1px solid ${marketingLine.hairline}`,
        borderBottom: `1px solid ${marketingLine.hairline}`,
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          // Pin below the sticky header — pinning at 0 slides the eyebrow
          // under the header chrome.
          top: `${marketingLayout.headerHeight}px`,
          height: `calc(100vh - ${marketingLayout.headerHeight}px)`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 2.5,
          px: 6,
          pt: 4,
          pb: 3,
        }}
      >
        <SceneHeading />

        {/*
          Two nested boxes on purpose: the outer one carries the scaled height
          so the flex column reserves the right space, while the inner one is
          authored at 645px and scaled. Putting the scaled height on the
          transformed element would render the scene at scale².
        */}
        <Box
          sx={{
            width: '100%',
            maxWidth: 960,
            height: `${Math.round(PIPELINE_STAGE_HEIGHT * stageScale)}px`,
            flex: '0 0 auto',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: `${PIPELINE_STAGE_HEIGHT}px`,
              transform: `scale(${stageScale})`,
              transformOrigin: 'top center',
            }}
          >
            {/* Track — stops where the storefront card begins */}
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                left: '50%',
                top: 0,
                height: 400,
                width: '2px',
                ml: '-1px',
                backgroundColor: marketingLine.hairline,
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: marketingColor.accent,
                  transformOrigin: 'top',
                  transform: `scaleY(${p})`,
                }}
              />
            </Box>

            <Box aria-hidden sx={{ ...dotSx(0.36), top: '196px' }} />
            <Box aria-hidden sx={{ ...dotSx(0.56), top: '330px' }} />

            {/* Camera travelling the pipeline */}
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: 0,
                transform: `translateX(-50%) ${cameraTransform(p)}`,
                transformOrigin: 'center top',
                zIndex: 3,
                willChange: 'transform',
              }}
            >
              <MarketingImageSlot
                src={marketingImages.pipelineCamera}
                fallbackSrc={marketingImages.pipelineCameraFallback}
                alt={t('public.home.pipeline.cameraAlt')}
                placeholder={t('public.home.pipeline.cameraPlaceholder')}
                sx={{
                  width: 280,
                  height: 280,
                  borderRadius: '10px',
                  border: `2px solid ${marketingColor.accent}`,
                  boxShadow: `${marketingShadow.card}, 0 0 30px rgba(95,226,138,0.15)`,
                }}
                imageSx={{ objectFit: 'contain', p: 4 }}
              />
              <Box
                sx={{
                  textAlign: 'center',
                  mt: 1.25,
                  fontFamily: marketingFont.mono,
                  fontSize: 11.5,
                  color: marketingColor.textMuted,
                  opacity: 1 - ramp(p, 0.14, 0.24),
                }}
              >
                raw_input.jpg · entering pipeline
              </Box>
            </Box>

            {/* Structured JSON, left of the line */}
            <Box
              sx={{
                ...codeCardSx,
                position: 'absolute',
                right: '52%',
                top: '150px',
                mr: 4.5,
                width: 250,
                border: `1px solid ${marketingLine.hairlineStrong}`,
                opacity: jsonReveal * settle,
                transform: `translateX(${lerp(jsonReveal, -24, 0)}px)`,
                zIndex: 2,
              }}
            >
              <StructuredCard />
            </Box>

            {/* AI enrichment, right of the line */}
            <Box
              sx={{
                ...codeCardSx,
                position: 'absolute',
                left: '52%',
                top: '284px',
                ml: 4.5,
                width: 270,
                border: `1px solid ${marketingColor.accent}`,
                boxShadow: '0 0 24px rgba(95,226,138,0.18)',
                opacity: aiReveal * settle,
                transform: `translateX(${lerp(aiReveal, 24, 0)}px)`,
                zIndex: 2,
              }}
            >
              <EnrichedCard />
            </Box>

            {/* Live storefront listing */}
            <Box
              sx={{
                position: 'absolute',
                left: '50%',
                top: '388px',
                width: 620,
                maxWidth: '94%',
                transform: `translateX(-50%) translateY(${lerp(shopReveal, 40, 0)}px) scale(${lerp(
                  shopReveal,
                  0.92,
                  1
                )})`,
                opacity: shopReveal,
                zIndex: 1,
              }}
            >
              <StorefrontCard />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
